import {
  DatePicker,
  Input,
  Divider,
  Typography,
  Button,
  Form,
  InputNumber,
  Row,
  Col,
  message,
} from 'antd';
import moment from 'moment-timezone';

const SGT_TZ = 'Asia/Singapore';
const toSgt = (date) =>
  date ? moment.tz(date.format('YYYY-MM-DD HH:mm:ss'), SGT_TZ) : null;
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import _l from 'lodash';
import { fetchScreens } from 'state/actions/screens';
import {
  fetchAvailability,
  clearAvailabilityState,
} from 'state/actions/slot';
import {
  createTemplate,
  fetchTemplateById,
  updateTemplate,
  setTemplate,
} from 'state/actions/template';
import { fetchMedia } from 'state/actions/media';
import { fetchProgrammaticVendors } from 'state/actions/programmaticVendor';
import { setActiveTab } from 'state/actions/tab';
import TimelineSequence from 'components/TimelineSequence';
import ScreenDrawer from 'components/ScreenDrawer';
import ProgrammaticVendorDrawer from 'components/ProgrammaticVendorDrawer';
import { TrashIcon } from 'assets/icon/dynamic';
import './index.css';
import './index.scss';

const { Title } = Typography;

const SELECTABLE_MEDIA_STATUS = ['published', 'unpublished'];

const initialFormState = {
  name: '',
  description: '',
  start: null,
  end: null,
  siteId: '',
  screenIds: [],
  totalDuration: 60,
  slots: [],
};

function TemplateForm({ isEditing }) {
  const { siteId, siteName, templateId: templateIdFromParams } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [formTemplate, setFormTemplate] = useState(() => ({
    ...initialFormState,
    siteId,
  }));
  const [errors, setErrors] = useState({});
  const [screenAllocations, setScreenAllocations] = useState([]);
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [isVendorDrawerOpen, setIsVendorDrawerOpen] = useState(false);

  const {
    mediaList,
    screenList,
    templateFromState,
    templateLoading,
    programmaticVendors,
    role,
    userData,
  } = useSelector(
    (state) => ({
      mediaList: state.media.list,
      screenList: state.screens.list,
      templateFromState: state.template.template,
      templateLoading: state.template.loading,
      programmaticVendors: state.programmaticVendor.list,
      role: state.auth.userData?.role,
      userData: state.auth.userData,
    }),
    shallowEqual
  );

  const templateAccess = userData?.roleAccess?.access?.find(
    (acc) => acc.siteId === siteId
  )?.template;
  const canManage =
    role === 'superadmin' ||
    !!(isEditing ? templateAccess?.update : templateAccess?.create);

  useEffect(() => {
    dispatch(fetchScreens({ siteId }));
    dispatch(fetchMedia({ siteId, categoryId: 'all' }));
    // Vendors are site-wide, not per template, so the list is the same on the
    // create page and every template's edit page.
    dispatch(fetchProgrammaticVendors({ siteId }));
    if (isEditing && templateIdFromParams) {
      dispatch(fetchTemplateById({ siteId, id: templateIdFromParams }));
    } else {
      dispatch(setTemplate(null));
      setFormTemplate({ ...initialFormState, siteId });
      setScreenAllocations([]);
    }
  }, [dispatch, siteId, isEditing, templateIdFromParams]);

  const screenIds = useMemo(
    () => screenAllocations.map((s) => s._id),
    [screenAllocations]
  );

  useEffect(() => {
    if (
      isEditing &&
      templateFromState?._id === templateIdFromParams &&
      !templateLoading &&
      screenList.length > 0
    ) {
      const newFormTemplate = {
        name: templateFromState.name || '',
        description: templateFromState.description || '',
        start: templateFromState.start
          ? moment.unix(templateFromState.start).tz(SGT_TZ)
          : null,
        end: templateFromState.end
          ? moment.unix(templateFromState.end).tz(SGT_TZ)
          : null,
        siteId: templateFromState.siteId || siteId,
        screenIds: templateFromState.screenIds || [],
        totalDuration: templateFromState.totalDuration || 60,
        slots: templateFromState.slots || [],
      };

      const screenAlloc = screenList.filter((s) =>
        (templateFromState.screenIds || []).includes(s._id)
      );
      setScreenAllocations(screenAlloc);
      setFormTemplate(newFormTemplate);
    }
  }, [
    isEditing,
    templateIdFromParams,
    templateFromState,
    siteId,
    templateLoading,
    screenList,
  ]);

  const debouncedCheckAvailability = useCallback(
    _l.debounce((sIds, startDate, endDate) => {
      dispatch(
        fetchAvailability(
          sIds,
          startDate,
          endDate,
          null,
          null,
          isEditing,
          templateIdFromParams
        )
      );
    }, 750),
    [dispatch, isEditing, templateIdFromParams]
  );

  useEffect(() => {
    if (screenIds.length > 0 && formTemplate.start && formTemplate.end) {
      debouncedCheckAvailability(
        screenIds,
        moment(formTemplate.start).format('YYYY-MM-DD'),
        moment(formTemplate.end).format('YYYY-MM-DD')
      );
    } else {
      dispatch(clearAvailabilityState());
    }
  }, [
    screenIds,
    formTemplate.start,
    formTemplate.end,
    debouncedCheckAvailability,
    dispatch,
  ]);

  const handleTopLevelChange = (name, value) =>
    setFormTemplate((prev) => ({ ...prev, [name]: value }));

  const handleFormChange = (name, value) =>
    setFormTemplate((prev) => ({ ...prev, [name]: value }));

  const handleRemoveAllocation = (allocationId) =>
    setScreenAllocations((prev) =>
      prev.filter((a) => a._id !== allocationId)
    );

  const handleSubmit = async () => {
    let isValid = true;
    const newErrors = {};
    if (!formTemplate.name?.trim()) {
      newErrors.name = 'Name cannot be empty';
      isValid = false;
    }
    if (!screenAllocations || screenAllocations.length === 0) {
      newErrors.screens = 'Please assign at least one screen.';
      isValid = false;
    }
    if (!formTemplate.start) {
      newErrors.startDate = 'Start date is required';
      isValid = false;
    }
    if (!formTemplate.end) {
      newErrors.endDate = 'End date is required';
      isValid = false;
    }
    const programmaticSlotWithoutVendor = (formTemplate.slots || []).find(
      (s) => s.mediaType === 'PROGRAMMATIC' && !s.programmaticVendorId
    );
    if (programmaticSlotWithoutVendor) {
      newErrors.slots = `Slot "${
        programmaticSlotWithoutVendor.name || programmaticSlotWithoutVendor.slotNumber
      }" is programmatic but has no vendor selected.`;
      message.error(newErrors.slots);
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) return;

    const basePayload = {
      name: formTemplate.name,
      description: formTemplate.description,
      siteId: formTemplate.siteId,
      screenIds,
      start: formTemplate.start ? formTemplate.start.unix() : null,
      end: formTemplate.end ? formTemplate.end.unix() : null,
      totalDuration: formTemplate.totalDuration,
      // Whole-template save: the backend reconciles the slots array, including
      // mediaType / provider / programmaticVendorId per slot.
      slots: (formTemplate.slots || []).map((s) => ({
        ...s,
        mediaIds: (s.mediaIds || []).map((m) =>
          typeof m === 'string' ? m : m._id
        ),
      })),
    };

    try {
      if (isEditing) {
        await dispatch(
          updateTemplate({ ...basePayload, id: templateIdFromParams })
        );
      } else {
        await dispatch(createTemplate(basePayload));
      }
      dispatch(setActiveTab({ tab: 'template' }));
      history.push(`/${siteId}/information/${siteName}/`);
    } catch (submitError) {
      console.error('Submit Error:', submitError);
      message.error('Failed to save template. Please check the form for errors.');
    }
  };

  const renderConfigurationForm = () => (
    <div>
      <Row gutter={32} style={{ marginBottom: '24px' }}>
        <Col xs={24} md={12}>
          <div className="form">
            <div className="screen-allocation-header">
              <label className="form-label" style={{ fontWeight: '600' }}>
                Screen Allocation
              </label>
              <button
                type="button"
                className="add-allocation-link"
                onClick={() => setIsAllocationModalOpen(true)}
              >
                + Add Allocation
              </button>
            </div>
            <div className="screen-allocation">
              {screenAllocations.length === 0 ? (
                <div className="empty-state">
                  <p className="empty-text">No allocation added.</p>
                  {errors.screens && (
                    <p
                      style={{
                        color: '#ff4d4f',
                        fontSize: '12px',
                        marginTop: '8px',
                        fontWeight: '500',
                      }}
                    >
                      {errors.screens}
                    </p>
                  )}
                </div>
              ) : (
                screenAllocations.map((allocation) => (
                  <div key={allocation._id} className="screen-item">
                    <span className="screen-name">{allocation.name}</span>
                    <div className="screen-controls">
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => handleRemoveAllocation(allocation._id)}
                        >
                          <TrashIcon size={14} color="#CB3A31" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '6px',
              border: '1px solid #e8e8e8',
              marginTop: '16px',
            }}
          >
            <Title level={5} style={{ color: '#0851B5', marginBottom: '20px' }}>
              Duration Setup
            </Title>
            <InputNumber
              addonBefore="Duration"
              addonAfter="Seconds"
              style={{ width: '100%' }}
              min={1}
              value={formTemplate.totalDuration}
              onChange={(value) => handleFormChange('totalDuration', value)}
            />
          </div>
        </Col>
      </Row>

      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '6px',
          border: '1px solid #e8e8e8',
          marginTop: '16px',
        }}
      >
        <TimelineSequence
          duration={formTemplate.totalDuration || 60}
          mediaList={(() => {
            const expanded = (mediaList || [])
              .flatMap((m) =>
                m.grouped && m.grouped.length > 0 ? m.grouped : [m]
              )
              // Only published/unpublished are assignable; archived stays out
              .filter((media) => SELECTABLE_MEDIA_STATUS.includes(media.status));
            if (screenIds.length === 0) return expanded;
            return expanded.filter((media) =>
              (media.screenIds || []).some((id) =>
                typeof id === 'string'
                  ? screenIds.includes(id)
                  : screenIds.includes(id?._id)
              )
            );
          })()}
          allMediaList={mediaList || []}
          programmaticVendors={programmaticVendors || []}
          initialSlots={formTemplate.slots || []}
          onSlotsChange={(slots) => handleFormChange('slots', slots)}
        />
      </div>
    </div>
  );

  if (!canManage) {
    return <Redirect to={`/${siteId}/information/${siteName}`} />;
  }

  return (
    <div className="template-form">
      <Form layout="vertical" onFinish={handleSubmit}>
        <Row gutter={32}>
          <Col xs={24} md={12}>
            <Title level={5} style={{ color: '#0851B5' }}>
              Template Details
            </Title>
            <Form.Item
              label="Template Name"
              required
              validateStatus={errors.name ? 'error' : ''}
              help={errors.name}
            >
              <Input
                placeholder="Enter template's name"
                value={formTemplate.name}
                onChange={(e) => handleTopLevelChange('name', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Description">
              <Input.TextArea
                placeholder="Description"
                value={formTemplate.description}
                onChange={(e) =>
                  handleTopLevelChange('description', e.target.value)
                }
                rows={4}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Title level={5} style={{ color: '#0851B5' }}>
              General Scheduling
            </Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Start Date"
                  required
                  validateStatus={errors.startDate ? 'error' : ''}
                  help={errors.startDate}
                >
                   <DatePicker
                    style={{ width: '100%' }}
                    value={formTemplate.start}
                    onChange={(date) => handleTopLevelChange('start', toSgt(date))}
                    format="DD/MM/YYYY HH:mm"
                    showTime
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="End Date"
                  required
                  validateStatus={errors.endDate ? 'error' : ''}
                  help={errors.endDate}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    value={formTemplate.end}
                    onChange={(date) => handleTopLevelChange('end', toSgt(date))}
                    format="DD/MM/YYYY HH:mm"
                    showTime
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <Title level={4} style={{ color: '#0851B5', margin: 0 }}>
            Content & Timeline Configuration
          </Title>
          <Button onClick={() => setIsVendorDrawerOpen(true)}>
            Programmatic Vendors
          </Button>
        </div>
        <div
          style={{
            backgroundColor: '#fafbfc',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #e8e8e8',
          }}
        >
          {renderConfigurationForm()}
        </div>
        <div
          style={{
            marginTop: '32px',
            textAlign: 'right',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '24px',
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={() => history.goBack()}>
            Cancel
          </Button>
          <Button
            type="primary"
            className="btn-primary"
            htmlType="submit"
            loading={templateLoading}
          >
            {isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </div>
      </Form>

      <ScreenDrawer
        open={isAllocationModalOpen}
        onClose={() => setIsAllocationModalOpen(false)}
        onSubmit={(screens) => setScreenAllocations(screens)}
        initialScreens={screenIds}
      />

      <ProgrammaticVendorDrawer
        open={isVendorDrawerOpen}
        onClose={() => setIsVendorDrawerOpen(false)}
        siteId={siteId}
      />
    </div>
  );
}

export default TemplateForm;
